import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import {
  FaTachometerAlt,
  FaBox,
  FaHospital,
  FaAppleAlt,
  FaBook,
  FaShoppingCart,
  FaUsers,
  FaEnvelope,
  FaTags,
  FaChartBar,
  FaCog,
  FaChevronDown,
  FaChevronRight,
  FaSignOutAlt,
  FaUserCircle,
  FaHome
} from 'react-icons/fa';
import { GiMedicinePills } from 'react-icons/gi';

const AdminSidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [expandedMenus, setExpandedMenus] = useState({
    products: false,
    hospital: false,
    dietPlans: false,
    ebooks: false,
    orders: false,
    users: false,
    offers: false,
    reports: false
  });

  const toggleMenu = (menu) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menu]: !prev[menu]
    }));
  };

  const menuItems = [
    {
      id: 'dashboard',
      title: 'Dashboard',
      icon: <FaTachometerAlt />,
      path: '/admin/dashboard',
      submenu: []
    },
    {
      id: 'products',
      title: 'Manage Products',
      icon: <FaBox />,
      path: null,
      submenu: [
        { title: 'Add Product', path: '/admin/products/add' },
        { title: 'View Products', path: '/admin/products' },
      ]
    },
    {
      id: 'hospital',
      title: 'Manage Hospital',
      icon: <FaHospital />,
      path: null,
      submenu: [
        { title: 'Hospital Details', path: '/admin/hospital/details' },
        { title: 'Appointment Booking', path: '/admin/appointment/booking' },
      ]
    },
    {
      id: 'dietPlans',
      title: 'Manage Diet Plans',
      icon: <FaAppleAlt />,
      path: null,
      submenu: [
        { title: 'Add Diet Plan', path: '/admin/diet-plans/add' },
        { title: 'View Diet Plans', path: '/admin/diet-plans' },
      ]
    },
    {
      id: 'ebooks',
      title: 'Manage E-Books',
      icon: <FaBook />,
      path: null,
      submenu: [
        { title: 'Add E-Book', path: '/admin/ebooks/add' },
        { title: 'View E-Books', path: '/admin/ebooks' },
      ]
    },
    {
      id: 'orders',
      title: 'Orders',
      icon: <FaShoppingCart />,
      path: null,
      submenu: [
        { title: 'View Orders', path: '/admin/orders' },
        { title: 'Payment Status', path: '/admin/orders/payment' }
      ]
    },
    {
      id: 'users',
      title: 'Users',
      icon: <FaUsers />,
      path: '/admin/users',
      submenu: []
    },
    {
      id: 'contact',
      title: 'Contact',
      icon: <FaEnvelope />,
      path: '/admin/contact',
      submenu: []
    },
    {
      id: 'Home Page',
      title: 'Home Page',
      icon: <FaTags />,
      path: '/admin/home',
      submenu: []
    },
    {
      id: 'blogs',
      title: 'Blogs',
      icon: <FaChartBar />,
      path: '/admin/blogs',
      submenu: []
    },
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = () => {
    logout();          // AuthContext: clears user state + sessionStorage + localStorage
    navigate('/login');
  };

  return (
    <div className="fixed left-0 top-0 h-screen bg-gradient-to-b from-teal-900 via-emerald-900 to-teal-800 text-white w-64 z-30 flex flex-col">
      {/* Admin Header */}
      <div className="p-4 border-b border-teal-700">
        <div className="flex items-center space-x-3">
          <div className="bg-white p-2 rounded-xl shadow-lg">
            <div className="h-8 w-8 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-lg flex items-center justify-center">
              <GiMedicinePills className="text-lg text-white" />
            </div>
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Admin Panel</h2>
            <p className="text-xs text-teal-300">Management System</p>
          </div>
        </div>
      </div>

      {/* Menu Items */}
      <div className="flex-1 overflow-y-auto py-4">
        <nav className="space-y-1 px-3">
          {menuItems.map((item) => (
            <div key={item.id}>
              {item.submenu.length > 0 ? (
                <div className="mb-1">
                  <button
                    onClick={() => toggleMenu(item.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-300 ${
                      expandedMenus[item.id] 
                        ? 'bg-teal-700/50 text-white' 
                        : 'hover:bg-teal-800/50 text-teal-100 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{item.icon}</span>
                      <span className="font-medium">{item.title}</span>
                    </div>
                    {expandedMenus[item.id] ? (
                      <FaChevronDown className="text-sm" />
                    ) : (
                      <FaChevronRight className="text-sm" />
                    )}
                  </button>
                  
                  {/* Submenu */}
                  {expandedMenus[item.id] && (
                    <div className="ml-4 mt-1 space-y-1 border-l-2 border-teal-600/50 pl-4">
                      {item.submenu.map((subItem, index) => (
                        <Link
                          key={index}
                          to={subItem.path}
                          className={`block py-2.5 px-3 rounded-lg text-sm transition-all duration-300 ${
                            isActive(subItem.path)
                              ? 'bg-teal-600 text-white font-medium'
                              : 'text-teal-200 hover:bg-teal-800/30 hover:text-white'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className="w-1.5 h-1.5 rounded-full bg-teal-400"></div>
                            <span>{subItem.title}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to={item.path}
                  className={`flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 ${
                    isActive(item.path)
                      ? 'bg-gradient-to-r from-teal-600 to-emerald-600 text-white shadow-lg'
                      : 'hover:bg-teal-800/50 text-teal-100 hover:text-white'
                  }`}
                >
                  <span className="text-lg">{item.icon}</span>
                  <span className="font-medium">{item.title}</span>
                  {item.title === 'Dashboard' && (
                    <span className="ml-auto text-xs bg-teal-500 px-2 py-1 rounded-full">New</span>
                  )}
                </Link>
              )}
            </div>
          ))}
        </nav>
      </div>

      {/* Footer - Logout */}
      <div className="p-4 border-t border-teal-700">
        <button
          onClick={handleLogout}
          className="w-full flex items-center justify-center space-x-2 p-3 bg-gradient-to-r from-red-600/20 to-pink-600/20 hover:from-red-600/30 hover:to-pink-600/30 rounded-lg transition-all duration-300 border border-red-700/30 group"
        >
          <FaSignOutAlt className="text-red-400 group-hover:text-red-300" />
          <span className="font-medium text-red-300 group-hover:text-red-200">Logout</span>
        </button>
        
        <div className="mt-3 text-center">
          <p className="text-xs text-teal-400">Version 2.1.0</p>
          <p className="text-xs text-teal-500 mt-1">© 2024 Anusaya Platform</p>
        </div>
      </div>

      {/* Mobile Toggle Button (hidden on desktop) */}
      <button className="lg:hidden fixed bottom-4 right-4 bg-teal-600 text-white p-3 rounded-full shadow-lg">
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
    </div>
  );
};

export default AdminSidebar;